export const BE_SWITCHER_STORAGE_KEY = 'beSwitcher';
export const BE_SWITCHER_PREVIOUS_KEY = 'beSwitcherPrevious';
export const BE_SWITCHER_ERROR_KEY = 'beSwitcherError';
export const DEFAULT_CUSTOM_NAME = 'Vlastné';
export const FEATURE_BRANCH_TITLE_PREFIX = ' [FB]';
export const BE_SWITCHER_UNREACHABLE_MESSAGE = 'Nefunkčná URL.';
export const BE_SWITCHER_PROBE_TIMEOUT_MS = 8000;
const WELCOME_SEEN_STORAGE_KEY = 'welcomeSeen';

/** Clears localStorage but keeps preferences that must survive logout. */
export const clearLocalStoragePreservingAppKeys = (extraItems?: Record<string, string>): void => {
  const language = localStorage.getItem('language');
  const theme = localStorage.getItem('cmd-theme');
  const beSwitcher = localStorage.getItem(BE_SWITCHER_STORAGE_KEY);
  const welcomeSeen = localStorage.getItem(WELCOME_SEEN_STORAGE_KEY);
  localStorage.clear();
  if (language) {
    localStorage.setItem('language', language);
  }
  if (theme === 'light' || theme === 'dark') {
    localStorage.setItem('cmd-theme', theme);
  }
  if (beSwitcher) {
    localStorage.setItem(BE_SWITCHER_STORAGE_KEY, beSwitcher);
  }
  if (welcomeSeen) {
    localStorage.setItem(WELCOME_SEEN_STORAGE_KEY, welcomeSeen);
  }
  if (extraItems) {
    Object.entries(extraItems).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  }
};

export type BESwitcherMode = 'dev' | 'custom';

export interface BESwitcherState {
  mode: BESwitcherMode;
  customName: string;
  customUrl: string;
}

export const isBESwitcherEnabled = (): boolean => import.meta.env.VITE_SENTRY_ENV !== 'production';

export const normalizeBackendUrl = (url: string): string => url.trim().replace(/\/+$/, '');

export const isValidBackendUrl = (url: string): boolean => {
  try {
    const normalized = normalizeBackendUrl(url);
    if (!normalized) {
      return false;
    }
    const parsed = new URL(normalized);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

export const getBESwitcherState = (): BESwitcherState | null => {
  try {
    const raw = localStorage.getItem(BE_SWITCHER_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as BESwitcherState;
    if (parsed?.mode !== 'dev' && parsed?.mode !== 'custom') {
      return null;
    }
    return {
      mode: parsed.mode,
      customName: parsed.customName?.trim() || DEFAULT_CUSTOM_NAME,
      customUrl: parsed.customUrl || '',
    };
  } catch {
    return null;
  }
};

export const saveBESwitcherState = (state: BESwitcherState): void => {
  localStorage.setItem(
    BE_SWITCHER_STORAGE_KEY,
    JSON.stringify({
      mode: state.mode,
      customName: state.customName.trim() || DEFAULT_CUSTOM_NAME,
      customUrl:
        state.mode === 'custom' ? normalizeBackendUrl(state.customUrl) : state.customUrl || '',
    })
  );
};

export const getDefaultBESwitcherState = (): BESwitcherState => ({
  mode: 'dev',
  customName: DEFAULT_CUSTOM_NAME,
  customUrl: '',
});

export const resolveBackendUrl = (envUrl: string): string => {
  if (!isBESwitcherEnabled()) {
    return normalizeBackendUrl(envUrl);
  }

  const state = getBESwitcherState();
  if (state?.mode === 'custom' && isValidBackendUrl(state.customUrl)) {
    return normalizeBackendUrl(state.customUrl);
  }

  return normalizeBackendUrl(envUrl);
};

export const resolvePageTitlePrefix = (envPrefix: string | undefined): string => {
  if (!isBESwitcherEnabled()) {
    return envPrefix || '';
  }

  const state = getBESwitcherState();
  if (state?.mode === 'custom' && isValidBackendUrl(state.customUrl)) {
    return FEATURE_BRANCH_TITLE_PREFIX;
  }

  return envPrefix || '';
};

export const getActiveBESwitcherDisplay = (
  envBackendUrl: string
): { title: string; url: string; mode: BESwitcherMode } => {
  const state = getBESwitcherState() || getDefaultBESwitcherState();
  const envUrl = normalizeBackendUrl(envBackendUrl);

  if (state.mode === 'custom' && isValidBackendUrl(state.customUrl)) {
    return {
      mode: 'custom',
      title: state.customName || DEFAULT_CUSTOM_NAME,
      url: normalizeBackendUrl(state.customUrl),
    };
  }

  return {
    mode: 'dev',
    title: 'Staging',
    url: envUrl,
  };
};

export const isCustomBackendActive = (): boolean => {
  if (!isBESwitcherEnabled()) {
    return false;
  }

  const state = getBESwitcherState();
  return state?.mode === 'custom' && isValidBackendUrl(state.customUrl);
};

export const snapshotBESwitcherState = (): void => {
  const current = getBESwitcherState() || getDefaultBESwitcherState();
  localStorage.setItem(BE_SWITCHER_PREVIOUS_KEY, JSON.stringify(current));
};

const isSameBackendState = (left: BESwitcherState, right: BESwitcherState): boolean =>
  left.mode === right.mode &&
  left.customName.trim() === right.customName.trim() &&
  normalizeBackendUrl(left.customUrl) === normalizeBackendUrl(right.customUrl);

export const restorePreviousBESwitcherState = (): void => {
  const current = getBESwitcherState();
  const raw = localStorage.getItem(BE_SWITCHER_PREVIOUS_KEY);
  localStorage.removeItem(BE_SWITCHER_PREVIOUS_KEY);

  if (!raw) {
    saveBESwitcherState(getDefaultBESwitcherState());
    return;
  }

  try {
    const parsed = JSON.parse(raw) as BESwitcherState;
    if (parsed?.mode === 'dev' || parsed?.mode === 'custom') {
      const previous: BESwitcherState = {
        mode: parsed.mode,
        customName: parsed.customName?.trim() || DEFAULT_CUSTOM_NAME,
        customUrl: parsed.customUrl || '',
      };

      if (current && isSameBackendState(current, previous)) {
        saveBESwitcherState(getDefaultBESwitcherState());
        return;
      }

      saveBESwitcherState(previous);
      return;
    }
  } catch {
    // Fall through to the default environment.
  }

  saveBESwitcherState(getDefaultBESwitcherState());
};

export const setBESwitcherUnreachableError = (): void => {
  localStorage.setItem(BE_SWITCHER_ERROR_KEY, BE_SWITCHER_UNREACHABLE_MESSAGE);
};

export const consumeBESwitcherError = (): string | null => {
  const message = localStorage.getItem(BE_SWITCHER_ERROR_KEY);
  if (message) {
    localStorage.removeItem(BE_SWITCHER_ERROR_KEY);
  }
  return message;
};

export const revertUnreachableBackend = (): void => {
  setBESwitcherUnreachableError();
  restorePreviousBESwitcherState();
  window.location.reload();
};

/** Any HTTP response means the host exists. Network/timeout failure does not. */
export const probeBackendUrl = async (
  url: string,
  timeoutMs = BE_SWITCHER_PROBE_TIMEOUT_MS
): Promise<boolean> => {
  if (!isValidBackendUrl(url)) {
    return false;
  }

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    // no-cors: opaque success still means the host answered. CORS would otherwise
    // look like a dead URL even when the backend is up.
    await fetch(`${normalizeBackendUrl(url)}/api/v1/auth/sign-in`, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timer);
  }
};
